import React, { FC, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { routes } from "@app/routing";
import { CancerStage } from "@app/provider/types";
import { GisIntakeStep, STEP_ROUTE_SUFFIX } from "@app/service/intakeForm";
import { GIS_NO_ORDER_UID } from "@app/utils/gisEntrySource";
import { IntakeFormStepLayout } from "../components";
import { useIntakeForm } from "../context";

const messages = defineMessages({
  title: {
    id: "intakeStepCancerType.title",
    defaultMessage: "What type of cancer do you have?",
  },
  colonRectal: {
    id: "intakeStepCancerType.colonRectal",
    defaultMessage: "Colon or Rectal",
  },
  breast: {
    id: "intakeStepCancerType.breast",
    defaultMessage: "Breast",
  },
  ovarian: {
    id: "intakeStepCancerType.ovarian",
    defaultMessage: "Ovarian",
  },
  endometrialUterine: {
    id: "intakeStepCancerType.endometrialUterine",
    defaultMessage: "Endometrial or Uterine",
  },
  melanoma: {
    id: "intakeStepCancerType.melanoma",
    defaultMessage: "Melanoma",
  },
  esophageal: {
    id: "intakeStepCancerType.esophageal",
    defaultMessage: "Esophageal",
  },
  lung: {
    id: "intakeStepCancerType.lung",
    defaultMessage: "Lung",
  },
  prostate: {
    id: "intakeStepCancerType.prostate",
    defaultMessage: "Prostate",
  },
  gastricStomach: {
    id: "intakeStepCancerType.gastricStomach",
    defaultMessage: "Gastric or Stomach",
  },
  sarcoma: {
    id: "intakeStepCancerType.sarcoma",
    defaultMessage: "Sarcoma",
  },
  lymphoma: {
    id: "intakeStepCancerType.lymphoma",
    defaultMessage: "Lymphoma",
  },
  noCancer: {
    id: "intakeStepCancerType.noCancer",
    defaultMessage: "I do not have cancer",
  },
  other: {
    id: "intakeStepCancerType.other",
    defaultMessage: "Other",
  },
  otherInputLabel: {
    id: "intakeStepCancerType.otherInputLabel",
    defaultMessage: "Other cancer type",
  },
  otherPlaceholder: {
    id: "intakeStepCancerType.otherPlaceholder",
    defaultMessage: "Please specify your cancer type",
  },
  otherErrorRequired: {
    id: "intakeStepCancerType.otherErrorRequired",
    defaultMessage: "Please enter your cancer type.",
  },
  otherErrorTooShort: {
    id: "intakeStepCancerType.otherErrorTooShort",
    defaultMessage: "Please enter at least 2 characters.",
  },
  otherErrorTooLong: {
    id: "intakeStepCancerType.otherErrorTooLong",
    defaultMessage: "Please keep this under {max} characters.",
  },
  otherErrorInvalidChars: {
    id: "intakeStepCancerType.otherErrorInvalidChars",
    defaultMessage:
      "Please use only letters, spaces, and basic punctuation (- ' , . / & ( )).",
  },
  nextButton: {
    id: "intakeStepCancerType.nextButton",
    defaultMessage: "Next",
  },
});

// Stable option keys for the radio list.
const OPTION_KEYS = [
  "colonRectal",
  "breast",
  "ovarian",
  "endometrialUterine",
  "melanoma",
  "esophageal",
  "lung",
  "prostate",
  "gastricStomach",
  "sarcoma",
  "lymphoma",
  "noCancer",
  "other",
] as const;
type OptionKey = (typeof OPTION_KEYS)[number];

// The no-order flow reuses the prefilled `cancerType` string field, which is
// the same field a backing order would populate. `buildAcuitySchedulingUrl`
// maps that value through `CANCER_TYPE_LABELS[value.toUpperCase()]`, so we
// persist the canonical Acuity code here (NOT the display label) to stay
// compatible with order-backed flows. `noCancer` persists `null` (the Acuity
// "Cancer Type" multibox has no "none" option — that signal is carried by the
// cancer-stage field instead); `other` persists the free-text value.
const OPTION_CODES: Record<Exclude<OptionKey, "other">, string | null> = {
  colonRectal: "COLORECTAL",
  breast: "BREAST",
  ovarian: "OVARIAN",
  endometrialUterine: "ENDOMETRIAL",
  melanoma: "MELANOMA",
  esophageal: "ESOPHAGEAL",
  lung: "LUNG",
  prostate: "PROSTATE",
  gastricStomach: "GASTRIC",
  sarcoma: "SARCOMA",
  lymphoma: "LYMPHOMA",
  noCancer: null,
};

// Validation bounds for the free-text "Other" cancer type. The value is
// free text that ultimately flows into the Acuity scheduling URL, so we keep
// it to plausible cancer-type wording: letters, spaces, and the punctuation
// that shows up in real type names (e.g. "Non-Hodgkin's", "Head/Neck").
const OTHER_MIN_LENGTH = 2;
const OTHER_MAX_LENGTH = 255;
// Letters (ASCII + common accented Latin ranges), spaces, and a small set of
// punctuation. Must also contain at least one letter so input isn't only
// punctuation/whitespace. (Explicit ranges instead of \p{L} since the build
// targets es5, where the regex unicode flag is unavailable.)
const OTHER_ALLOWED_PATTERN = /^[a-zA-Z\u00c0-\u024f\s'\-/(),.&]+$/;
const OTHER_HAS_LETTER_PATTERN = /[a-zA-Z\u00c0-\u024f]/;

type OtherErrorKey =
  | "otherErrorRequired"
  | "otherErrorTooShort"
  | "otherErrorTooLong"
  | "otherErrorInvalidChars";

const validateOtherText = (raw: string): OtherErrorKey | null => {
  const value = raw.trim();
  if (value.length === 0) return "otherErrorRequired";
  if (value.length < OTHER_MIN_LENGTH) return "otherErrorTooShort";
  if (value.length > OTHER_MAX_LENGTH) return "otherErrorTooLong";
  if (!OTHER_HAS_LETTER_PATTERN.test(value)) return "otherErrorInvalidChars";
  if (!OTHER_ALLOWED_PATTERN.test(value)) return "otherErrorInvalidChars";
  return null;
};

const IntakeStepCancerType: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const {
    prefilledData,
    updatePrefilledField,
    setCurrentStep,
    totalSteps,
    getNextStep,
    getPreviousStep,
  } = useIntakeForm();

  const currentStep = GisIntakeStep.CANCER_TYPE;
  const isNoOrder = orderUidPublic === GIS_NO_ORDER_UID;

  const optionLabels: Record<OptionKey, string> = {
    colonRectal: intl.formatMessage(messages.colonRectal),
    breast: intl.formatMessage(messages.breast),
    ovarian: intl.formatMessage(messages.ovarian),
    endometrialUterine: intl.formatMessage(messages.endometrialUterine),
    melanoma: intl.formatMessage(messages.melanoma),
    esophageal: intl.formatMessage(messages.esophageal),
    lung: intl.formatMessage(messages.lung),
    prostate: intl.formatMessage(messages.prostate),
    gastricStomach: intl.formatMessage(messages.gastricStomach),
    sarcoma: intl.formatMessage(messages.sarcoma),
    lymphoma: intl.formatMessage(messages.lymphoma),
    noCancer: intl.formatMessage(messages.noCancer),
    other: intl.formatMessage(messages.other),
  };

  // Reconstruct the prior selection from the persisted cancerType code.
  const initialFromPrefill = (): {
    key: OptionKey | null;
    otherText: string;
  } => {
    const stored = prefilledData?.cancerType;
    if (!stored) {
      // No type stored. A persisted "no cancer" choice clears the type (Acuity
      // has no "none" type option) and instead sets the stage to OTHER, so use
      // that to re-select "I do not have cancer" on back-navigation.
      if (prefilledData?.cancerStage === CancerStage.OTHER) {
        return { key: "noCancer", otherText: "" };
      }
      return { key: null, otherText: "" };
    }
    const code = stored.toUpperCase().trim();
    const match = OPTION_KEYS.find(
      (key) => key !== "other" && OPTION_CODES[key] === code
    );
    if (match) return { key: match, otherText: "" };
    return { key: "other", otherText: stored };
  };

  const initial = initialFromPrefill();
  const [selectedKey, setSelectedKey] = useState<OptionKey | null>(initial.key);
  const [otherText, setOtherText] = useState<string>(initial.otherText);
  // Only surface the inline error once the user has left the field (or tried to
  // continue), so we don't flag input while they're still typing.
  const [otherTouched, setOtherTouched] = useState<boolean>(false);

  const otherErrorKey =
    selectedKey === "other" ? validateOtherText(otherText) : null;

  useEffect(() => {
    setCurrentStep(currentStep);
  }, [setCurrentStep]);

  const buildRoute = (step: GisIntakeStep | null) =>
    routes.scheduleAGeneticInformationSessionIntakePage(
      orderUidPublic || "",
      testUid || ""
    ) + (step ? STEP_ROUTE_SUFFIX[step] : "");

  const persist = (key: OptionKey, text: string) => {
    if (key === "other") {
      updatePrefilledField("cancerType", text.trim() || null);
      return;
    }
    updatePrefilledField("cancerType", OPTION_CODES[key]);
  };

  const handleSelect = (key: OptionKey) => {
    setSelectedKey(key);
    if (key !== "other") {
      setOtherTouched(false);
    }
    persist(key, otherText);
  };

  const handleOtherTextChange = (value: string) => {
    setOtherText(value);
    if (selectedKey === "other") {
      // Persist only valid input; otherwise clear so a stale/invalid value
      // never reaches the Acuity URL while Next is gated.
      const isValid = validateOtherText(value) === null;
      updatePrefilledField("cancerType", isValid ? value.trim() : null);
    }
  };

  const isContinueDisabled =
    selectedKey === null || (selectedKey === "other" && otherErrorKey !== null);

  const handleContinue = () => {
    if (isContinueDisabled) {
      if (selectedKey === "other") setOtherTouched(true);
      return;
    }
    navigate(buildRoute(getNextStep(currentStep)));
  };

  const handleBack = () => {
    // Cancer type is the first oncology-specific screen after the no-order
    // test picker. Route back to that picker explicitly instead of relying on
    // the active step sequence, which may not yet include the no-order steps
    // while guest context is being restored.
    if (isNoOrder) {
      navigate(buildRoute(GisIntakeStep.SELECT_TEST));
      return;
    }
    navigate(buildRoute(getPreviousStep(currentStep)));
  };

  if (!isNoOrder) {
    return <Navigate to={buildRoute(GisIntakeStep.CONFIRM_INFO)} replace />;
  }

  return (
    <IntakeFormStepLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={intl.formatMessage(messages.title)}
      onContinue={handleContinue}
      onBack={handleBack}
      continueDisabled={isContinueDisabled}
      continueLabel={intl.formatMessage(messages.nextButton)}
    >
      <div className="intake-form-step__radio-list">
        {OPTION_KEYS.map((key) => (
          <label key={key} className="intake-form-step__radio-list__item">
            <input
              type="radio"
              name="cancer-type"
              className="intake-form-step__radio-list__input"
              checked={selectedKey === key}
              onChange={() => handleSelect(key)}
            />
            <span className="intake-form-step__radio-list__label">
              {optionLabels[key]}
            </span>
          </label>
        ))}
      </div>
      {selectedKey === "other" && (
        <div className="intake-form-step__field-group">
          <input
            type="text"
            className={`intake-form-step__text-input${
              otherTouched && otherErrorKey
                ? " intake-form-step__text-input--error"
                : ""
            }`}
            value={otherText}
            onChange={(e) => handleOtherTextChange(e.target.value)}
            onBlur={() => setOtherTouched(true)}
            placeholder={intl.formatMessage(messages.otherPlaceholder)}
            aria-label={intl.formatMessage(messages.otherInputLabel)}
            aria-invalid={otherTouched && otherErrorKey ? true : undefined}
            aria-describedby={
              otherTouched && otherErrorKey
                ? "cancer-type-other-error"
                : undefined
            }
            maxLength={OTHER_MAX_LENGTH}
          />
          {otherTouched && otherErrorKey && (
            <p
              id="cancer-type-other-error"
              className="intake-form-step__error-text"
              role="alert"
            >
              {intl.formatMessage(messages[otherErrorKey], {
                max: OTHER_MAX_LENGTH,
              })}
            </p>
          )}
        </div>
      )}
    </IntakeFormStepLayout>
  );
};

export default IntakeStepCancerType;
